import { apiFetch } from './client';

/**
 * Procedures — the scheduled work the runtime owns, and its run history.
 *
 * **`health` can be absent and `last_run_status` can be null, and those
 * are different facts.** A procedure registered but never dispatched has
 * no run history at all; one that ran and is still running has a status
 * of neither success nor failure. The predecessor's Workflows view
 * collapsed both into "unknown" and a never-dispatched trigger looked
 * like a healthy idle one — which is the defect `/api/findings` reports
 * as `procedure_health / never_ran`.
 *
 * **Status is `succeeded`, not `success`.** Written down because a query
 * against the wrong literal shows every procedure failing, and it looks
 * like a real outage rather than a typo.
 */

export type RunStatus = 'succeeded' | 'failed' | 'running' | string;

export interface ProcedureHealth {
  last_run_status: RunStatus | null;
  last_run_at_ms: number | null;
  last_run_duration_ms: number | null;
  consecutive_failures: number;
  /** Recorded runs that made a model, agent or judge call (machinai 0.29.0). */
  runs_with_model_calls?: number;
}

export interface Procedure {
  name: string;
  owner_module: string;
  trigger_kind: string;
  trigger: Record<string, unknown>;
  /** What registration declared. Defaults to `mechanical` in the runtime. */
  cost_class: string;
  /**
   * What the run records show (machinai 0.29.0): `llm` if any run made a
   * model, agent or judge call, `mechanical` if it has runs and none did,
   * `null` if it has never run. Absent from an older runtime.
   */
  cost_class_observed?: string | null;
  health?: ProcedureHealth | null;
}

export interface ProcedureRun {
  id: string;
  procedure_name: string;
  trigger_kind: string;
  status: RunStatus;
  started_at_ms: number;
  finished_at_ms: number | null;
  duration_ms: number | null;
  error: string | null;
  payload: Record<string, unknown> | null;
  agent_invocations: unknown[];
  judge_calls: unknown[];
  narrate_calls: unknown[];
}

export function fetchProcedures(): Promise<{
  procedures: Procedure[];
  count: number;
}> {
  return apiFetch('/api/procedures');
}

export function fetchProcedureRuns(q: {
  procedure_name?: string;
  status?: string;
  since_ms?: number;
  limit?: number;
} = {}): Promise<{ runs: ProcedureRun[]; count: number }> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v !== undefined && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return apiFetch(`/api/procedures/runs${qs ? `?${qs}` : ''}`);
}

/**
 * Run a procedure now.
 *
 * A manual run is a *write*, and the only one this page makes. It is
 * POST for that reason and the button that calls it says so.
 */
export function runProcedure(name: string): Promise<ProcedureRun> {
  return apiFetch<ProcedureRun>(
    `/api/procedures/${encodeURIComponent(name)}/run`,
    { method: 'POST' },
  );
}
